// src/components/Detail/ImageGallery.js

import React, { useState, useCallback, useRef, useEffect } from 'react';
import Modal from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';

const ImageGallery = ({
    imagenes,
    titulo,
    imagenActual,
    onImagenChange,
    colorSeleccionado,
    onColorChange,
    colores
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // Estados para el deslizamiento
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const imageRef = useRef(null);

    const openModal = useCallback((index) => {
        onImagenChange(index);
        setIsModalOpen(true);
    }, [onImagenChange]);

    const closeModal = () => setIsModalOpen(false);

    // Constantes para el deslizamiento
    const minSwipeDistance = 50;

    // Funciones para cambiar imágenes
    const siguienteImagen = useCallback(() => {
        const nuevaImagen = imagenActual === imagenes.length - 1 ? 0 : imagenActual + 1;
        onImagenChange(nuevaImagen);
    }, [imagenes.length, imagenActual, onImagenChange]);

    const anteriorImagen = useCallback(() => {
        const nuevaImagen = imagenActual === 0 ? imagenes.length - 1 : imagenActual - 1;
        onImagenChange(nuevaImagen);
    }, [imagenes.length, imagenActual, onImagenChange]);

    // ELIMINADO: Ya no cambiamos el color desde las miniaturas
    // Solo navegamos entre imágenes

    // Manejadores de eventos táctiles
    const handleTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            siguienteImagen();
        } else if (isRightSwipe) {
            anteriorImagen();
        }
    };

    // Manejadores de eventos de mouse para desktop
    let startX = 0;
    let isDragging = false;

    const handleMouseDown = (e) => {
        isDragging = true;
        startX = e.clientX;
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
    };

    const handleMouseUp = (e) => {
        if (!isDragging) return;
        isDragging = false;
        
        const endX = e.clientX;
        const distance = startX - endX;
        
        if (Math.abs(distance) > minSwipeDistance) {
            if (distance > 0) {
                siguienteImagen();
            } else {
                anteriorImagen();
            }
        }
    };

    // Navegación con teclado
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                anteriorImagen();
            } else if (e.key === 'ArrowRight') {
                siguienteImagen();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [anteriorImagen, siguienteImagen]);

    if (!imagenes || imagenes.length === 0) {
        return (
            <div className="modern-image-section">
                <div className="modern-main-image-container">
                    <div className="modern-no-image">Sin imágenes</div>
                </div>
            </div>
        );
    }
    
    return (
        <>
            <aside className="modern-image-section">

                {/*  <h1 className="modern-product-title">{titulo}</h1> */}
                <div className="modern-image-main">
                    {/* Miniaturas laterales (solo para desktop) */}
                    <div className="modern-thumbnails modern-thumbnails-side">
                        {imagenes.slice(0, 6).map((img, index) => (
                            <div
                                key={img.idImagen || index}
                                className={`modern-thumbnail ${imagenActual === index ? 'active' : ''}`}
                                onClick={() => onImagenChange(index)}
                            >
                                <img src={img.imagen} alt={`${titulo} - vista ${index + 1}`} />
                                {img.colorHex && (
                                    <div
                                        className="thumbnail-color-indicator"
                                        style={{ backgroundColor: img.colorHex }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="modern-main-image-container">
                    <div 
                        ref={imageRef}
                        className="modern-main-image" 
                        onClick={() => openModal(imagenActual)}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        style={{ cursor: 'grab' }}
                    >
                        <img
                            src={imagenes[imagenActual]?.imagen}
                            alt={titulo}
                            draggable={false}
                        />
                        
                        {/* Botones de navegación */}
                        {imagenes.length > 1 && (
                            <>
                                <button 
                                    className="image-nav-btn prev-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        anteriorImagen();
                                    }}
                                    aria-label="Imagen anterior"
                                >
                                    &#8249;
                                </button>
                                <button 
                                    className="image-nav-btn next-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        siguienteImagen();
                                    }}
                                    aria-label="Siguiente imagen"
                                >
                                    &#8250;
                                </button>
                            </>
                        )}
                        
                        {/* Indicadores de página */}
                        {imagenes.length > 1 && (
                            <div className="image-indicators">
                                {imagenes.map((_, index) => (
                                    <button
                                        key={index}
                                        className={`indicator ${imagenActual === index ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onImagenChange(index);
                                        }}
                                        aria-label={`Ir a imagen ${index + 1}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                </div>

                {/* Miniaturas inferiores (responsive) */}
                <div className="modern-thumbnails modern-thumbnails-bottom">
                    {imagenes.map((img, index) => (
                        <div
                            key={img.idImagen || index}
                            className={`modern-thumbnail ${imagenActual === index ? 'active' : ''}`}
                            onClick={() => {
                                onImagenChange(index);
                                // Solo navegamos entre imágenes, no cambiamos el color
                            }}
                        >
                            <img src={img.imagen} alt={`${titulo} - vista ${index + 1}`} />
                            {img.colorHex && (
                                <div
                                    className="thumbnail-color-indicator"
                                    style={{ backgroundColor: img.colorHex }}
                                    title={`Color: ${img.color}`}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </aside>

            <Modal open={isModalOpen} onClose={closeModal} center classNames={{ modal: 'modern-modal' }}>
                <div 
                    className="modal-image-container"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                >
                    <img
                        src={imagenes[imagenActual]?.imagen}
                        alt={`${titulo} - vista ampliada`}
                        draggable={false}
                    />
                    
                    {/* Botones de navegación en modal */}
                    {imagenes.length > 1 && (
                        <>
                            <button 
                                className="modal-nav-btn modal-prev-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    anteriorImagen();
                                }}
                                aria-label="Imagen anterior"
                            >
                                &#8249;
                            </button>
                            <button 
                                className="modal-nav-btn modal-next-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    siguienteImagen();
                                }}
                                aria-label="Siguiente imagen"
                            >
                                &#8250;
                            </button>
                        </>
                    )}
                    
                    {/* Indicadores en modal */}
                    {imagenes.length > 1 && (
                        <div className="modal-indicators">
                            {imagenes.map((_, index) => (
                                <button
                                    key={index}
                                    className={`modal-indicator ${imagenActual === index ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onImagenChange(index);
                                    }}
                                    aria-label={`Ir a imagen ${index + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default React.memo(ImageGallery);